import { ax, db } from "@/core";
import { ProjectRequestWonForm } from "@/forms/project-request-won-form";
import {
	type ProjectRequestWonFormType,
	projectRequestWonFormSchema,
} from "@/forms/project-request-won-form/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ProjectRequest } from "@msuite/katana";
import {
	Button,
	Modal,
	ModalBody,
	ModalButtonGroup,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useDocument,
} from "@msuite/picasso";
import { doc, getDoc } from "firebase/firestore";
import type { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";

/** Props Interface */
interface AddProjectRequestWonModalProps {
	isOpen: boolean;
	onClose: () => void;
	projectRequestId: string;
	onComplete: () => void;
}

export const AddProjectRequestWonModal: FC<AddProjectRequestWonModalProps> = ({
	isOpen,
	onClose,
	projectRequestId,
	onComplete,
}) => {
	/** Hooks */
	const methods = useForm<ProjectRequestWonFormType>({
		resolver: zodResolver(projectRequestWonFormSchema),
		defaultValues: async () => {
			const projectRequestRef = doc(db, `projectRequests/${projectRequestId}`);
			const projectRequest = (await getDoc(projectRequestRef)).data() as ProjectRequest;
			const contractSum = projectRequest.offerSum ?? 0;
			return {
				contractSum,
			};
		},
	});

	const { data: projectRequest } = useDocument<ProjectRequest>(db, {
		path: `projectRequests/${projectRequestId}`,
		subscribe: true,
	});

	/** Functions */
	async function handleOnSubmit() {
		try {
			await ax.patch(
				`/v2/project-requests/${projectRequestId}/final-result?result=win&contractSum=${methods.getValues().contractSum}`,
			);
			handleOnClose();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnClose() {
		onComplete();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
			>
				<ModalOverlay />
				<form onSubmit={methods.handleSubmit(handleOnSubmit)}>
					<ModalContent>
						<ModalCloseButton />
						<ModalHeader>Als "Gewonnen" markieren</ModalHeader>
						<ModalBody>
							{!methods.formState.isLoading && (
								<ProjectRequestWonForm projectRequest={projectRequest} />
							)}
						</ModalBody>
						<ModalFooter>
							<ModalButtonGroup>
								<Button
									variant="ghost"
									onClick={handleOnClose}
								>
									abbrechen
								</Button>
								<Button
									type="submit"
									isLoading={methods.formState.isSubmitting}
								>
									Als "Gewonnen" markieren
								</Button>
							</ModalButtonGroup>
						</ModalFooter>
					</ModalContent>
				</form>
			</Modal>
		</FormProvider>
	);
};
